import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { filter, BehaviorSubject, combineLatest, take } from 'rxjs';

import { BaseService } from './base.service';
import { UserProfileService } from './userProfile.service';
import { FormService } from 'core';
import { NavigationEnd, Router } from '@angular/router';
import { appRoutesMapping } from '../app-routing.module';
import { SKIP_DEFAULT_ERROR_HANDLER } from '../app.module';

declare global {
  interface Window {
    dataLayer: {
      push: (event: Record<string, unknown>) => number;
      [index: number]: Record<string, unknown>;
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService extends BaseService {
  _analyticsInstance = null;
  currentRoute$$ = new BehaviorSubject<{ name: string; url: string } | null>(null);
  uuid$$ = new BehaviorSubject(null);
  uuid$ = this.uuid$$.asObservable();
  uuid;

  constructor(
    override http: HttpClient,
    private userProfileService: UserProfileService,
    private formService: FormService,
    private router: Router,
  ) {
    super(http);
  }

  init() {
    this._analyticsInstance = AnalyticsBrowser.load({ writeKey: environment.ANALYTICS_KEY });
    this.formService.setAnalytics(this.on.bind(this));
    this.identifyUser();

    const routeEntries = Object.entries(appRoutesMapping);
    this.router.events.subscribe((value) => {
      if (value instanceof NavigationEnd) {
        const currentRoute = routeEntries.find(([_, route]) => `/${route}` === value.url);

        if (currentRoute) {
          const trackRoute = {
            name: currentRoute.at(0),
            url: currentRoute.at(1),
          };
          this.currentRoute$$.next(trackRoute);
        } else {
          const currentRoute = routeEntries.find(([_, route]) => value?.url?.includes(route));
          const trackRoute = {
            name: currentRoute.at(0),
            url: currentRoute.at(1),
          };
          this.currentRoute$$.next(trackRoute);
        }

        this.on('page', 'Page viewed', {
          path: window.location.pathname,
          referrer: document.referrer,
          url: window.location.search,
          title: document.title,
          browser: navigator.userAgent,
        });
      }
    });
  }

  identifyUser() {
    this.userProfileService.userData$
      .pipe(
        filter((x) => !!x?.email),
        take(1),
      )
      .subscribe((userData) => {
        this.http
          .post(
            environment.ANALYTICS_UUID_API,
            {},
            {
              params: { email: userData.email },
              headers: {
                [SKIP_DEFAULT_ERROR_HANDLER]: 'true',
              },
            },
          )
          .subscribe((uuid) => {
            this.uuid = uuid;
            this._analyticsInstance.identify(uuid, {
              firstName: userData.firstName,
              lastName: userData.lastName,
              email: userData.email,
            });
          });
      });
  }

  on(action: 'track' | 'page', eventName, extraArgs) {
    const self = this;

    if (self._analyticsInstance === null) return;

    const args = {
      product: 'GeoNavigator',
      module: self.currentRoute$$?.getValue()?.name,
      screen_name: window.location.href,
      ...extraArgs,
    };

    if (this.uuid) {
      args.uuid = this.uuid;
    }

    self._analyticsInstance[action](eventName, args);
  }

  public get analytics() {
    return this._analyticsInstance;
  }

  // -- Separate user analytics for Amplitude and HotJar

  private addGtmScript = (gtmId: string) => {
    const script = document.createElement('script');
    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
				new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
				j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
				'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
				})(window,document,'script','dataLayer', '${gtmId}');`;

    document.head.insertBefore(script, document.head.childNodes[0]);
  };

  private addGtmNoScriptIframe = (gtmId: string) => {
    const noScript = document.createElement('noscript');
    noScript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
            height="0" width="0" style="display:none;visibility:hidden"></iframe>`;

    document.body.insertBefore(noScript, document.body.childNodes[0]);
  };

  private gtmEvents = {
    login: 'geonavigator_user_login',
  };

  public pushGtmEvent = (eventType: string, payload: unknown) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventType,
      params: payload,
      _clear: true,
    });
  };

  private pushUserLoginGtmEvent = () => {
    this.userProfileService.userData$
      .pipe(
        filter((x) => !!x?.email),
        take(1),
      )
      .subscribe((userData) => {
        this.pushGtmEvent(this.gtmEvents.login, {
          user_id: userData.email,
        });
      });
  };

  private handleGTMEvent = () => {
    window.addEventListener('message', (event) => {
      const { data, origin } = event;

      if (!data || typeof data !== 'object' || data.type !== 'gtm_event' || !data.data) return;

      const allowedOrigins = [
        'http://localhost:3000',
        'https://localhost:3000',
        'http://localhost:4200',
        'https://localhost:4200',
        'https://geonavigator.cbre.com',
        'https://geonavigator-dev.cbre.com',
        'https://geonavigator-uat.cbre.com',
        'https://cbre-lbr-nlytcs-stg.carto.solutions',
        'https://cbre-lbr-nlytcs.carto.solutions',
      ];

      if (!allowedOrigins.includes(origin)) {
        console.warn('GTM: Blocked event from unauthorized origin:', origin);
        return;
      }

      const { site_selection_report_type } = data.data.params;
      const reportTypes = ['Benchmark', 'Cost calculation'];

      if (!reportTypes.includes(site_selection_report_type)) return;

      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(data.data);
        console.log('GTM: Event received from child application and pushed to dataLayer:', data.data);
      } catch (error) {
        console.error('GTM: Error processing child application GTM event:', error);
      }
    });
  };

  public initGTM(gtmId: string) {
    this.addGtmScript(gtmId);
    this.addGtmNoScriptIframe(gtmId);
    this.pushUserLoginGtmEvent();
    this.handleGTMEvent();
  }
}
